// Generated by GitHub Copilot
/**
 * Feature Toggle Hooks - React hooks for easy feature toggle integration
 *
 * These hooks provide a clean, React-friendly interface for checking feature toggles
 * throughout the application. They integrate with the FeatureToggleService and provide
 * real-time updates when toggle states change.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import featureToggleService from "../services/featureToggleService";

/**
 * Hook to check if a single feature is enabled
 * @param {string} featureName - Name of the feature to check
 * @param {Object} user - User object (optional, will use service's user if not provided)
 * @returns {Object} - { isEnabled: boolean, isLoading: boolean, error: Error|null, refresh: Function }
 */
export const useFeatureToggle = (featureName, user = null) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  // Set user in service if provided
  useEffect(() => {
    if (user && user !== featureToggleService.user) {
      featureToggleService.setUser(user);
    }
  }, [user]);

  const checkFeature = useCallback(async () => {
    if (!featureName) {
      setIsEnabled(false);
      setIsLoading(false);
      setError(new Error("Feature name is required"));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const enabled = await featureToggleService.isFeatureEnabled(featureName);

      if (mountedRef.current) {
        setIsEnabled(enabled);
        setIsLoading(false);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        setIsEnabled(false);
        setIsLoading(false);
      }
    }
  }, [featureName]);

  // Check feature from cache without triggering refresh
  const checkFeatureFromCache = useCallback(() => {
    if (!featureName || !mountedRef.current) {
      return;
    }

    try {
      // Check cache directly without triggering refresh
      const globalDefinition =
        featureToggleService.globalDefinitions.get(featureName);

      if (globalDefinition !== undefined && !globalDefinition.enabled) {
        setIsEnabled(false);
        return;
      }

      const userFeature = featureToggleService.cachedFeatures.get(featureName);
      if (userFeature !== undefined) {
        setIsEnabled(userFeature);
        return;
      }

      // Fallback to user.features
      if (featureToggleService.user?.features?.hasOwnProperty(featureName)) {
        setIsEnabled(featureToggleService.user.features[featureName]);
        return;
      }

      setIsEnabled(false);
    } catch (err) {
      // Silent fail for cache reads
      setIsEnabled(false);
    }
  }, [featureName]);

  // Initial check
  useEffect(() => {
    checkFeature();
  }, [checkFeature]);

  // Subscribe to cache changes - only read from cache, don't trigger refresh
  useEffect(() => {
    const unsubscribe = featureToggleService.subscribe(() => {
      if (mountedRef.current) {
        checkFeatureFromCache();
      }
    });

    return () => {
      unsubscribe();
      mountedRef.current = false;
    };
  }, [checkFeatureFromCache]);

  const refresh = useCallback(() => {
    featureToggleService.invalidateCache();
  }, []);

  return {
    isEnabled,
    isLoading,
    error,
    refresh,
  };
};

/**
 * Hook to check multiple features at once
 * @param {Array<string>} featureNames - Array of feature names to check
 * @param {Object} user - User object (optional)
 * @returns {Object} - { features: Object, isLoading: boolean, error: Error|null, refresh: Function }
 */
export const useFeatureToggles = (featureNames = [], user = null) => {
  const [features, setFeatures] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  // Set user in service if provided
  useEffect(() => {
    if (user && user !== featureToggleService.user) {
      featureToggleService.setUser(user);
    }
  }, [user]);

  const checkFeatures = useCallback(async () => {
    if (!Array.isArray(featureNames) || featureNames.length === 0) {
      setFeatures({});
      setIsLoading(false);
      setError(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const featureStates = await featureToggleService.checkMultipleFeatures(
        featureNames
      );

      if (mountedRef.current) {
        setFeatures(featureStates);
        setIsLoading(false);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        setFeatures({});
        setIsLoading(false);
      }
    }
  }, [featureNames]);

  // Initial check
  useEffect(() => {
    checkFeatures();
  }, [checkFeatures]);

  // Subscribe to cache changes
  useEffect(() => {
    const unsubscribe = featureToggleService.subscribe(() => {
      if (mountedRef.current) {
        checkFeatures();
      }
    });

    return () => {
      unsubscribe();
      mountedRef.current = false;
    };
  }, [checkFeatures]);

  const refresh = useCallback(() => {
    featureToggleService.invalidateCache();
  }, []);

  return {
    features,
    isLoading,
    error,
    refresh,
  };
};

/**
 * Hook to get all enabled features for the current user
 * @param {Object} user - User object (optional)
 * @returns {Object} - { enabledFeatures: Array<string>, isLoading: boolean, error: Error|null, refresh: Function }
 */
export const useEnabledFeatures = (user = null) => {
  const [enabledFeatures, setEnabledFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  // Set user in service if provided
  useEffect(() => {
    if (user && user !== featureToggleService.user) {
      featureToggleService.setUser(user);
    }
  }, [user]);

  const fetchEnabledFeatures = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const features = await featureToggleService.getEnabledFeatures();

      if (mountedRef.current) {
        setEnabledFeatures(features);
        setIsLoading(false);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        setEnabledFeatures([]);
        setIsLoading(false);
      }
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchEnabledFeatures();
  }, [fetchEnabledFeatures]);

  // Subscribe to cache changes
  useEffect(() => {
    const unsubscribe = featureToggleService.subscribe(() => {
      if (mountedRef.current) {
        fetchEnabledFeatures();
      }
    });

    return () => {
      unsubscribe();
      mountedRef.current = false;
    };
  }, [fetchEnabledFeatures]);

  const refresh = useCallback(() => {
    featureToggleService.invalidateCache();
  }, []);

  return {
    enabledFeatures,
    isLoading,
    error,
    refresh,
  };
};

/**
 * Component wrapper for conditional rendering based on feature toggles
 * @param {Object} props - Component props
 * @param {string} props.feature - Feature name to check
 * @param {React.ReactNode} props.children - Content to render if feature is enabled
 * @param {React.ReactNode} props.fallback - Content to render if feature is disabled (optional)
 * @param {Object} props.user - User object (optional)
 * @returns {React.ReactElement} - Rendered content based on feature state
 */
export const FeatureToggle = ({
  feature,
  children,
  fallback = null,
  user = null,
}) => {
  const { isEnabled, isLoading, error } = useFeatureToggle(feature, user);

  if (isLoading) {
    return null; // Or a loading spinner if desired
  }

  if (error) {
    console.warn("FeatureToggle component error:", error);
    return fallback;
  }

  return isEnabled ? children : fallback;
};

/**
 * Higher-order component for feature toggle protection
 * @param {React.Component} WrappedComponent - Component to wrap
 * @param {string} requiredFeature - Feature required to render the component
 * @param {React.ReactNode} fallback - Component to render if feature is disabled
 * @returns {React.Component} - Wrapped component with feature toggle protection
 */
export const withFeatureToggle = (
  WrappedComponent,
  requiredFeature,
  fallback = null
) => {
  return function FeatureToggleWrapper(props) {
    const { isEnabled, isLoading, error } = useFeatureToggle(
      requiredFeature,
      props.user
    );

    if (isLoading) {
      return null; // Or a loading component
    }

    if (error) {
      console.warn("withFeatureToggle HOC error:", error);
      return fallback;
    }

    if (!isEnabled) {
      return fallback;
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Hook for debugging feature toggle state
 * @param {Object} user - User object (optional)
 * @returns {Object} - Debug information about feature toggle service
 */
export const useFeatureToggleDebug = (user = null) => {
  const [debugInfo, setDebugInfo] = useState({});
  const mountedRef = useRef(true);

  useEffect(() => {
    if (user && user !== featureToggleService.user) {
      featureToggleService.setUser(user);
    }
  }, [user]);

  useEffect(() => {
    const updateDebugInfo = () => {
      if (mountedRef.current) {
        setDebugInfo({
          cacheStats: featureToggleService.getCacheStats(),
          user: featureToggleService.user,
          timestamp: Date.now(),
        });
      }
    };

    // Initial update
    updateDebugInfo();

    // Subscribe to changes
    const unsubscribe = featureToggleService.subscribe(updateDebugInfo);

    return () => {
      unsubscribe();
      mountedRef.current = false;
    };
  }, []);

  return debugInfo;
};
